import type { SelectOption } from '../../../../components/inputs/Select';
import { uid } from '../../../../lib/uid';

/*
  Synthetic, and suffixed where a value is unique among live rows. Nothing here lands in the
  identity store — a nominee is an existing employee picked from the LIVE list.
*/

const DOCUMENTS: { name: string; description: string; defaultMaxPoints: number }[] = [
  { name: 'Nomination form', description: 'Signed by the nominating official.', defaultMaxPoints: 10 },
  { name: 'Performance rating', description: 'The latest rating period, certified by HR.', defaultMaxPoints: 20 },
  { name: 'Narrative of accomplishments', description: 'Two pages at most.', defaultMaxPoints: 30 },
  { name: 'Endorsement', description: 'From the head of the office.', defaultMaxPoints: 10 },
];

function pick<T>(items: readonly T[]): T | undefined {
  return items.length === 0 ? undefined : items[Math.floor(Math.random() * items.length)];
}

/** A category with two or three documents on its checklist. */
export function sampleAwardCategory() {
  const count = 2 + Math.floor(Math.random() * 2);

  return {
    name: `Sample award category ${uid().slice(0, 6)}`,
    description: 'Sample category. Delete it once the real ones are in.',
    documents: DOCUMENTS.slice(0, count),
  };
}

/** An award filled from the LIVE category picker, so its id is a row the server accepts. */
export function sampleAward(categories: SelectOption[]) {
  return {
    awardCategoryId: pick(categories)?.value ?? null,
    description: 'Sample award. Delete it once the real ones are in.',
    // A top 3 — the larger case, so the deliberation's cap is exercised rather than a single pick.
    winnerCount: 3,
  };
}

/**
 * An award's criteria: points for every DOCUMENT it is given (by id, from the live checklist) and two
 * criteria of its own, so the sheet a nominee is scored on is never only the papers.
 */
export function sampleCriteria(documentIds: string[]) {
  return {
    documents: Object.fromEntries(
      documentIds.map((id) => [id, { maxPoints: 10, description: 'Complete, signed and legible.' }]),
    ) as Record<string, { maxPoints: number; description: string }>,
    own: [
      { name: 'Impact on service', description: 'How far the work improved service to the public.', maxPoints: 30 },
      { name: 'Sustainability', description: 'Whether the improvement lasts without the nominee.', maxPoints: 20 },
    ],
  };
}

/** A score sheet: every criterion given a value between half and all of its maximum, in whole points. */
export function sampleScores(criteria: { id: string; maxPoints: number }[]): Record<string, number> {
  return Object.fromEntries(
    criteria.map((c) => [c.id, Math.ceil(c.maxPoints / 2) + Math.floor(Math.random() * (Math.floor(c.maxPoints / 2) + 1))]),
  );
}

/** A nomination filled from the LIVE staff picker. */
export function sampleNominee(staff: SelectOption[]) {
  return {
    personnelId: pick(staff)?.value ?? null,
    remarks: 'Sample nomination. Delete it once the real ones are in.',
  };
}
